import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, Divider, message, Spin, Button } from 'antd';
import axios from './utils/axiosConfig'; // 使用配置好的axios
import AuthService from './utils/auth';
import { LogoutOutlined } from '@ant-design/icons';

const FeishuLogin = () => {
  const [loading, setLoading] = useState(true);
  const [userInfo, setUserInfo] = useState({
    name: '',
    openId: '',
    userId: '',
    accessToken: '',
    refreshToken: '',
    tenantKey: '',
    avatarUrl: '',
  });

  // 从URL参数中获取 redirect_uri
  const getRedirectUri = useCallback(() => {
    // 优先从 sessionStorage 获取（OAuth 回调后会丢失 URL 参数）
    const storedRedirectUri = sessionStorage.getItem('sso_redirect_uri');
    if (storedRedirectUri) {
      return storedRedirectUri;
    }
    // 其次从 URL 参数获取
    const params = new URLSearchParams(window.location.search);
    return params.get('redirect_uri');
  }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps

  const initializedRef = useRef(false);

  // 初始化：检查URL参数并调用后端
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const initPage = useCallback(async (getTokenFn) => {
    try {
      // ⭐ 关键：先获取原始的 redirect_uri（从URL参数中获取）
      const originalRedirectUri = getRedirectUri();
      console.log('原始 redirect_uri:', originalRedirectUri);

      // 生产环境使用固定的回调地址，避免redirect_uri不一致导致飞书验证失败
      // 但如果URL中有 redirect_uri 参数，应该传递给后端
      const redirect_uri = process.env.REACT_APP_FEISHU_REDIRECT_URI || `${window.location.origin}/sso/api/v1/qrLogin`;

      const response = await axios.post('/sso/api/v1/qrLogin', {
        loginTime: `${new Date().getTime()}`,
        redirect_uri: redirect_uri,
        original_redirect_uri: originalRedirectUri, // ⭐ 传递原始 redirect_uri 给后端
        url: window.location.href,
      });

      if (response.code === 0) {
        const { tokenInfo, qrUserInfo } = response;

        // 保存用户信息
        setUserInfo({
          name: qrUserInfo.name || '',
          openId: qrUserInfo.openId || '',
          userId: qrUserInfo.userId || '',
          accessToken: tokenInfo.accessToken || '',
          refreshToken: tokenInfo.refreshToken || '',
          tenantKey: qrUserInfo.tenantKey || '',
          avatarUrl: qrUserInfo.avatarUrl || '',
        });

        // ⭐⭐ 关键：存储Token到Cookie（支持跨域名）⭐⭐
        if (tokenInfo.accessToken) {
          AuthService.setToken(tokenInfo.accessToken);
          console.log('Token已存储到Cookie，主域名: .markableai.cn');
          message.success('登录成功！Token已保存');
        }

        if (qrUserInfo.name) {
          message.success('已登录');
        }

        // ⭐ 如果有 redirect_uri，登录成功后跳转（使用原始 redirect_uri）
        if (originalRedirectUri) {
          console.log('登录成功，准备跳转至:', originalRedirectUri);
          setTimeout(() => {
            window.location.href = originalRedirectUri;
          }, 500);
        }
      }
    } catch (error) {
      console.error('初始化失败:', error);

      // 如果已经有Token，尝试用Token获取用户信息
      const savedToken = AuthService.getToken();
      if (savedToken && !window.location.search.includes('code=')) {
        message.info('initPage 检测到已保存的Token');
        // 这里可以调用一个获取用户信息的接口
        if (getTokenFn) {
          await getTokenFn(savedToken);
        }
      }
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 初始化二维码
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const initQRCode = useCallback(() => {
    if (!window.QRLogin) {
      message.error('SDK未加载，请刷新页面');
      return;
    }

    const appId = process.env.REACT_APP_FEISHU_APP_ID;
    // 生产环境使用固定的回调地址，避免redirect_uri不一致导致飞书验证失败
    const redirectUri = process.env.REACT_APP_FEISHU_REDIRECT_URI || `${window.location.origin}/qrLogin`;
    const gotoUrl = `https://passport.feishu.cn/suite/passport/oauth/authorize?client_id=${appId}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=code&state=success_login`;

    const qrInstance = window.QRLogin({
      id: 'qr_container',
      goto: gotoUrl,
      style: 'width: 280px; height: 280px;',
    });

    const handleMessage = (event) => {
      if (event.origin.includes('feishu.cn') && event.data?.tmp_code) {
        const tmpCode = event.data.tmp_code;
        console.log('收到tmp_code:', tmpCode);
        window.location.href = `${gotoUrl}&tmp_code=${tmpCode}`;
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
      if (qrInstance && qrInstance.destroy) {
        qrInstance.destroy();
      }
    };
  }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps

  const getUserInfoByToken = useCallback(async (token) => {
    console.log('尝试通过Token获取用户信息，Token:', token);
    try {
      setLoading(true);

      // 调用后端GET接口，传入access_token参数
      const response = await axios.get('/sso/api/v1/qrLogin', {
        params: {
          access_token: token
        }
      });
      console.log('Token查询响应:', response);
      if (response.code === 0 && response.userInfo) {
        const userData = response.userInfo;

        // 更新用户信息
        setUserInfo({
          name: userData.name || '',
          openId: userData.openId || '',
          userId: userData.userId || '',
          accessToken: userData.tokenInfo?.accessToken || token,
          refreshToken: userData.tokenInfo?.refreshToken || '',
          tenantKey: userData.tenantKey || '',
          avatarUrl: userData.avatarUrl || '',
        });

        message.success('用户信息获取成功');
        return true;
      } else {
        message.warning(response.msg || '获取用户信息失败');
        return false;
      }

    } catch (error) {
      console.error('通过Token获取用户信息失败:', error);

      // 401表示token无效或过期
      if (error.response?.status === 401) {
        message.error('登录已过期，请重新扫码登录');
        AuthService.clearToken(); // 清除无效的token
      } else {
        message.error('获取用户信息失败');
      }

      return false;
    } finally {
      setLoading(false);
    }
  }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps

  // 退出登录
  const logout = useCallback(() => {
    // 清除Token
    AuthService.clearToken();

    // 清除 sessionStorage 中的 redirect_uri
    sessionStorage.removeItem('sso_redirect_uri');

    // 清除用户信息
    setUserInfo({
      name: '',
      openId: '',
      userId: '',
      accessToken: '',
      refreshToken: '',
      tenantKey: '',
      avatarUrl: '',
    });

    message.info('已退出登录');

    // 清除URL参数，重新加载
    window.history.replaceState({}, '', '/qrLogin');
    setTimeout(() => {
      window.location.reload();
    }, 100);
  }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps

  useEffect(() => {
    const savedToken = AuthService.getToken();
    console.log('当前Token:', savedToken);

    // ⭐ 如果 URL 中有 redirect_uri，先保存到 sessionStorage
    const urlRedirectUri = new URLSearchParams(window.location.search).get('redirect_uri');
    if (urlRedirectUri) {
      sessionStorage.setItem('sso_redirect_uri', urlRedirectUri);
      console.log('redirect_uri 已保存到 sessionStorage:', urlRedirectUri);
    }

    const initialize = async () => {
      // 使用 useRef 避免触发重新渲染
      if (initializedRef.current) {
        console.log('已经初始化过，跳过');
        return;
      }
      initializedRef.current = true;

      if (savedToken) {
        message.info('检测到已保存的Token 1');
        const tokenValid = await getUserInfoByToken(savedToken);
        if (tokenValid) {
          setLoading(false);
          // 如果有 redirect_uri，也需要跳转
          const redirectUri = getRedirectUri();
          if (redirectUri) {
            console.log('已登录状态，准备跳转至:', redirectUri);
            setTimeout(() => {
              window.location.href = redirectUri;
            }, 500);
          }
          return;
        }
      }

      console.log('没有检测到Token，初始化登录流程');
      await initPage(getUserInfoByToken);

      // 延迟初始化二维码
      const timer = setTimeout(() => {
        if (!userInfo.name) {
          initQRCode();
        }
      }, 500);

      return () => clearTimeout(timer);
    };

    initialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initPage, getUserInfoByToken, initQRCode, userInfo.name]);

  const hasUserInfo = userInfo.name || userInfo.openId;

  return (
    <div style={{ maxWidth: 1200, margin: '40px auto', padding: 20 }}>
      <h1 style={{ textAlign: 'center', marginBottom: 40 }}>
        Markable SSO {hasUserInfo && <span style={{ color: '#52c41a' }}>✓ 已登录</span>}
      </h1>
      
      {/* Token状态显示 */}
      {/* <Card style={{ marginBottom: 20, background: currentToken ? '#f6ffed' : '#fff2e8' }}>
        <Row align="middle" justify="space-between">
          <Col>
            <div>
              <strong>Token状态: </strong>
              {currentToken ? (
                <span style={{ color: '#52c41a' }}>
                  ✅ 已保存（{currentToken.substring(0, 20)}...）
                  <br />
                  <small>主域名: {AuthService.getMainDomain()}</small>
                </span>
              ) : (
                <span style={{ color: '#fa8c16' }}>❌ 未保存</span>
              )}
            </div>
          </Col>
          <Col>
            {currentToken && (
              <Button 
                icon={<CopyOutlined />} 
                onClick={copyToken}
                style={{ marginRight: 10 }}
              >
                复制Token
              </Button>
            )}
            <Button 
              type="link" 
              onClick={() => console.log('当前Cookie:', document.cookie)}
              size="small"
            >
              查看Cookie
            </Button>
          </Col>
        </Row>
      </Card> */}
      
      {/* 跨域名测试（可选） */}
      {/* {process.env.NODE_ENV === 'development' && currentToken && (
        <Card style={{ marginBottom: 20 }}>
          <h3>跨子域名测试：</h3>
          <div style={{ marginTop: 10 }}>
            <Button onClick={() => goToOtherDomain('aaa')} style={{ marginRight: 10 }}>
              访问 aaa{AuthService.getMainDomain()}
            </Button>
            <Button onClick={() => goToOtherDomain('bbb')}>
              访问 bbb{AuthService.getMainDomain()}
            </Button>
          </div>
        </Card>
      )} */}
      
      <div style={{ display: 'flex', gap: 40, flexWrap: 'wrap' }}>
        <Card title="扫码登录" style={{ flex: 1, minWidth: 300 }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40 }}>
              <Spin size="large" tip="加载中..." />
            </div>
          ) : !hasUserInfo ? (
            <div style={{ textAlign: 'center' }}>
              <div id="qr_container" />
              <p style={{ marginTop: 20, color: '#666' }}>
                使用飞书APP扫码登录
              </p>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: 40, color: '#52c41a' }}>
              ✅ 已登录
            </div>
          )}
        </Card>

        <Card 
          title="用户信息" 
          style={{ flex: 1, minWidth: 300 }}
          extra={
            hasUserInfo && (
              <Button 
                type="link" 
                danger 
                icon={<LogoutOutlined />}
                onClick={logout}
              >
                退出登录
              </Button>
            )
          }
        >
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40 }}>
              <Spin size="large" tip="加载用户信息..." />
            </div>
          ) : hasUserInfo ? (
            <div>
              <div style={{ marginBottom: 20 }}>
                <p><strong>姓名:</strong> {userInfo.name}</p>
                <p><strong>Open ID:</strong> {userInfo.openId}</p>
                <p><strong>User ID:</strong> {userInfo.userId}</p>
                <p><strong>Tenant Key:</strong> {userInfo.tenantKey}</p>
              </div>
              
              <Divider />
              
              {/* <div style={{ marginBottom: 20 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <strong>Access Token:</strong>
                  <Button 
                    type="text" 
                    size="small" 
                    icon={<CopyOutlined />}
                    onClick={copyToken}
                  >
                    复制
                  </Button>
                </div>
                <Input.TextArea 
                  value={currentToken} 
                  rows={4}
                  readOnly
                  style={{ fontSize: 12 }}
                />
              </div> */}
              
              {/* {userInfo.refreshToken && (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                    <strong>Refresh Token:</strong>
                    <Button 
                      type="text" 
                      size="small" 
                      icon={<CopyOutlined />}
                      onClick={() => {
                        navigator.clipboard.writeText(userInfo.refreshToken)
                          .then(() => message.success('已复制'))
                          .catch(() => message.error('复制失败'));
                      }}
                    >
                      复制
                    </Button>
                  </div>
                  <Input.TextArea 
                    value={userInfo.refreshToken} 
                    rows={2}
                    readOnly
                    style={{ fontSize: 12 }}
                  />
                </div>
              )} */}
            </div>
          ) : (
            <div style={{ textAlign: 'center', color: '#999', padding: 40 }}>
              请扫码登录查看用户信息
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default FeishuLogin;